import { createClient } from '@supabase/supabase-js';

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || process.env.SUPABASE_URL;
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase environment variables are not set.');
}

// Use implicit flow on localhost to avoid PKCE 401; use env override for other hosts.
const isLocalhost =
  typeof window !== 'undefined' && window.location.hostname === 'localhost';
const authFlowType =
  isLocalhost || process.env.REACT_APP_SUPABASE_AUTH_FLOW_TYPE === 'implicit'
    ? 'implicit'
    : 'pkce';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: authFlowType,
    debug: process.env.NODE_ENV === 'development',
  },
  realtime: {
    params: {
      eventsPerSecond: 10, // Limit to stay within free tier
    },
  },
  global: {
    headers: {
      'X-Client-Info': 'car-transport-landing-page',
    },
  },
});

// Función para limpiar sesiones corruptas
export const clearAuthSession = () => {
  try {
    // Limpiar localStorage
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('supabase') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    
    // Limpiar sessionStorage
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
    
    console.log('🧹 Supabase sessions cleared');
  } catch (error) {
    console.error('Error clearing sessions:', error);
  }
};

// Función para verificar y corregir problemas de sesión
export const checkAndFixSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.warn('⚠️ Error retrieving session:', error.message);
      
      // Si hay error de clock skew, limpiar sesiones
      if (error.message.includes('future') || error.message.includes('clock')) {
        console.log('🕐 Clock skew detected, clearing sessions…');
        clearAuthSession();
        return false;
      }
    }
    
    return !!session;
  } catch (error) {
    console.error('Error verifying session:', error);
    return false;
  }
};

// Optional: Export session listener for other parts of the app
supabase.auth.onAuthStateChange((event, _session) => {
  try {
    switch (event) {
      case 'SIGNED_IN':
        // Log solo en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Signed in');
        }
        break;
      case 'TOKEN_REFRESHED':
        // Log solo en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 Token refreshed');
        }
        break;
      case 'SIGNED_OUT':
        // Log solo en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('🚪 User signed out');
        }
        break;
      case 'USER_UPDATED':
        // Log solo en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.log('📝 User updated');
        }
        break;
      default:
        // Solo log eventos importantes en producción
        if (process.env.NODE_ENV === 'development' || event === 'INITIAL_SESSION') {
          // No log para INITIAL_SESSION
        }
    }
  } catch (err) {
    console.error('Auth state change error:', err);
  }
});

// Verificar sesión al cargar la aplicación
if (typeof window !== 'undefined') {
  // Verificar sesión después de un breve delay para asegurar que todo esté cargado
  setTimeout(() => {
    checkAndFixSession();
  }, 1000);
}
